import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { authService } from '../../../../services/auth';
import './TOTPLoginStep.css';

const TOTPLoginStep = ({ tempToken, onSuccess, onBack, onError }) => {
  const { t } = useTranslation(['auth', 'common']);
  const [otpValue, setOtpValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!otpValue || otpValue.length !== 6) {
      return; // Don't show error, just return silently
    }

    try {
      setLoading(true);
      
      // Complete TOTP login
      const response = await authService.loginWithTOTP(tempToken, otpValue);
      
      // Store token and user data
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      onSuccess(response);
    } catch (error) {
      console.error('TOTP login error:', error);
      const errorMessage = error.response?.data?.message || t('auth:totp.verification_failed');
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtpValue(value);
    
    // Clear any previous errors when user starts typing
    if (value.length > 0) {
      onError('');
    }
    
    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      // Small delay to allow the user to see the complete code
      setTimeout(() => {
        // Double-check the current value to avoid race conditions
        setOtpValue(currentValue => {
          if (currentValue.length === 6) {
            handleVerify();
          }
          return currentValue;
        });
      }, 300); // Reduced delay for better UX
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otpValue.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="totp-login-step">
      <div className="totp-header">
        <div className="totp-icon">🔐</div>
        <h2 className="totp-title">
          {t('auth:totp.title')}
        </h2>
        <p className="totp-description">
          {t('auth:totp.description')}
        </p>
      </div>

      <div className="totp-form">
        <div className="otp-input-container">
          <label className="otp-label">
            {t('auth:totp.code_label')}
          </label>
          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            numInputs={6}
            separator={<span className="otp-separator">-</span>}
            inputType="number"
            shouldAutoFocus={true}
            inputStyle={{
              width: '45px',
              height: '50px',
              fontSize: '1.5rem',
              textAlign: 'center',
              border: '2px solid #dee2e6',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              backgroundColor: '#fff'
            }}
            containerStyle={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px'
            }}
            renderInput={(props) => <input {...props} onKeyPress={handleKeyPress} />}
          />
        </div>

        <div className="totp-actions">
          <button
            type="button"
            onClick={onBack}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common:buttons.back')}
          </button>
          <button
            type="button"
            onClick={handleVerify}
            className="btn btn-primary"
            disabled={loading || otpValue.length !== 6}
          >
            {loading ? t('auth:totp.verifying') : t('auth:totp.verify_button')}
          </button>
        </div>

        <div className="totp-help">
          <p>{t('auth:totp.help_text')}</p>
        </div>
      </div>
    </div>
  );
};

export default TOTPLoginStep;